/**
 * LinkedProductDevCard — advisory chip on PO detail.
 *
 * Read-only. A linked record is always labelled "Moved to PO"; its independent
 * development state is written alongside it so procurement handoff is never
 * mistaken for approval. This is advisory only and does not gate submission.
 * Renders nothing when there are no linked records, keeping ordinary POs quiet.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle2, Layers, ShoppingCart } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useLinkedDevForPurchaseOrder } from '../hooks/useProductDev';

interface Props {
  purchaseOrderId: string;
}

export function LinkedProductDevCard({ purchaseOrderId }: Props) {
  const { t } = useTranslation('productDev');
  const query = useLinkedDevForPurchaseOrder(purchaseOrderId);
  const rows = query.data ?? [];

  if (query.isLoading || rows.length === 0) return null;

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center gap-3 mb-2">
          <Layers className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{t('linkedCard.title')}</span>
        </div>
        <ul className="space-y-2">
          {rows.map((row) => {
            const developmentComplete = row.stage === 'bulk_complete' && row.status === 'approved' && row.priceAgreed;
            return (
              <li key={row.id} className="flex items-center gap-3 text-sm">
                <span
                  className={
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ' +
                    'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300'
                  }
                >
                  <ShoppingCart className="h-3 w-3" /> {t('linkedCard.movedToPo')}
                </span>
                <Link to={`/product-dev/${row.id}`} className="font-medium hover:underline truncate">
                  {row.style_name}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {developmentComplete && <CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-600" />}
                  {developmentComplete ? t('linkedCard.developmentComplete') : t('linkedCard.developmentContinues')}
                  {' · '}{t(`stage.${row.stage}`)} · {t(`status.${row.status}`)}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
